import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  HomeModernIcon,
  IdentificationIcon,
  AcademicCapIcon,
  InformationCircleIcon,
  CreditCardIcon,
  ChevronDownIcon,
  ArrowRightIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import { useTranslation } from '../i18n/LanguageContext';

const COLORS = {
  navy: '#0D1B2A',
  teal: '#00B4D8',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  grey: '#6B7280',
};

const NATIVE_LANGUAGES = [
  'English', 'Russian', 'Uzbek', 'Kazakh', 'Tajik', 'Kyrgyz', 'Other',
];

const LEARNING_GOALS = [
  'General English',
  'IELTS Preparation',
  'Business English',
  'Conversation Practice',
  'Academic English',
  "Children's English",
];

const PROGRAMME_INTERESTS = [
  'AI Practice Sessions',
  'Group Classes',
  'IELTS Group Intensive',
  'Business English / Corporate',
  'Undecided',
];

const REFERRAL_SOURCES = [
  'Friend/Family',
  'Social Media',
  'Google Search',
  'School Advertisement',
  'Walk-In',
  'Other',
];

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: `4px solid ${COLORS.teal}22`,
        borderTopColor: COLORS.teal,
      }}
    />
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 8,
  border: `1.5px solid #CBD5E1`,
  fontSize: 15,
  color: COLORS.navy,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: COLORS.navy,
  marginBottom: 5,
};

const fieldWrap = { marginBottom: 18 };

export default function Register() {
  const { t } = useTranslation();
  const [screen, setScreen] = useState(1);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [autoSelected, setAutoSelected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    phone: '',
    native_language: '',
    learning_goal: '',
    programme_interest: '',
    referral_source: '',
    message: '',
    identity_confirmed: false,
    learning_mode: 'remote',
  });
  const [registrationResult, setRegistrationResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);

  useEffect(() => {
    // Detect tenant subdomain or ?school= param
    const params = new URLSearchParams(window.location.search);
    const schoolParam = params.get('school');
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const isMain = parts.length <= 2 || parts[0] === 'www' || parts[0] === 'speakiq' || parts[0] === 'localhost';
    const tenantSlug = isMain ? schoolParam : parts[0];

    api.get('/api/public/schools')
      .then(res => {
        const list = res.data || [];
        setSchools(list);
        if (tenantSlug) {
          const match = list.find(s =>
            s.subdomain === tenantSlug ||
            s.name?.toLowerCase().replace(/\s+/g, '') === tenantSlug.toLowerCase()
          );
          if (match) {
            setSelectedSchool(match);
            setAutoSelected(true);
            setScreen(2);
          }
        }
      })
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));
  }, []);

  const cities = [...new Set(schools.map(s => s.city).filter(Boolean))].sort();
  const filteredSchools = selectedCity
    ? schools.filter(s => s.city === selectedCity)
    : schools;

  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
    setScreen(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.identity_confirmed) {
      setError('Please confirm that you understand you need to visit the centre in person.');
      return;
    }
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    // Send OTP to verify email before proceeding
    setOtpSending(true);
    try {
      await api.post('/api/public/send-registration-otp', {
        email: formData.email,
        name: formData.first_name,
      });
      setOtpSent(true);
      setOtpCode('');
      setScreen('otp');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to send verification code. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!otpCode || otpCode.length < 6) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/public/verify-registration-otp', {
        email: formData.email,
        otp: otpCode,
      });
      setOtpVerified(true);
      setScreen(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.error || 'Invalid code. Please check and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = async (mode) => {
    const selectedMode = mode || formData.learning_mode || 'remote';
    setFormData(prev => ({ ...prev, learning_mode: selectedMode }));
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...formData, learning_mode: selectedMode, school_id: selectedSchool.id, tenant_id: selectedSchool.id, photo_url: photoUrl || null };
      const res = await api.post('/api/public/register', payload);
      setRegistrationResult(res.data);
      setScreen(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const apiError = err?.response?.data?.error || err?.response?.data?.message || 'Submission failed. Please try again.';
      setError(apiError);
      setScreen(2);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8', fontFamily: "'Inter', sans-serif" }}>
      {/* Top bar */}
      <div style={{ background: COLORS.navy, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: COLORS.teal,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AcademicCapIcon style={{ width: 22, height: 22, color: '#fff' }} />
        </div>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 20, letterSpacing: '-0.5px' }}>SpeakIQ</span>
        <span style={{ color: COLORS.teal, fontSize: 13, marginLeft: 4 }}>English Learning Platform</span>
      </div>

      <AnimatePresence mode="wait">
        {screen === 1 && (
          <motion.div
            key="screen1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px' }}
          >
            {/* Hero heading */}
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <h1 style={{ fontSize: 36, fontWeight: 800, color: COLORS.navy, margin: '0 0 10px' }}>
                Find Your English Learning Centre
              </h1>
              <p style={{ fontSize: 17, color: COLORS.grey, margin: 0 }}>
                SpeakIQ powers English schools across Central Asia
              </p>
            </div>

            {/* Info banner */}
            <div style={{
              background: '#EFF6FF', border: `1px solid #BFDBFE`, borderRadius: 10,
              padding: '12px 18px', display: 'flex', alignItems: 'flex-start', gap: 10,
              marginBottom: 30, maxWidth: 700, margin: '0 auto 30px',
            }}>
              <InformationCircleIcon style={{ width: 20, height: 20, color: '#3B82F6', flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: 14, color: '#1E40AF' }}>
                Register for a specific school? You may have received a direct link from your centre.
              </p>
            </div>

            {/* City filter */}
            <div style={{ maxWidth: 320, margin: '0 auto 32px', position: 'relative' }}>
              <label style={{ ...labelStyle, textAlign: 'center', display: 'block', marginBottom: 8 }}>
                Filter by City
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedCity}
                  onChange={e => setSelectedCity(e.target.value)}
                  style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                >
                  <option value="">All cities</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDownIcon style={{
                  width: 18, height: 18, color: COLORS.grey,
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                }} />
              </div>
            </div>

            {/* Schools grid */}
            {loadingSchools ? <Spinner /> : (
              filteredSchools.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 24px', color: COLORS.grey }}>
                  <BuildingOffice2Icon style={{ width: 56, height: 56, color: '#CBD5E1', margin: '0 auto 16px' }} />
                  <p style={{ fontSize: 17, fontWeight: 600, color: COLORS.navy }}>Check back soon</p>
                  <p style={{ margin: 0, fontSize: 14 }}>More centres opening soon</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 24,
                }}>
                  {filteredSchools.map((school, i) => (
                    <motion.div
                      key={school.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      style={{
                        background: '#fff',
                        borderRadius: 14,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* Colour band */}
                      <div style={{
                        height: 6,
                        background: school.primary_colour || COLORS.teal,
                      }} />
                      <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Logo */}
                        <div style={{ marginBottom: 14 }}>
                          {school.logo_url ? (
                            <img
                              src={school.logo_url}
                              alt={school.name}
                              style={{ height: 52, objectFit: 'contain', maxWidth: '100%' }}
                            />
                          ) : (
                            <div style={{
                              width: 52, height: 52, borderRadius: 10,
                              background: (school.primary_colour || COLORS.teal) + '22',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <BuildingOffice2Icon style={{ width: 28, height: 28, color: school.primary_colour || COLORS.teal }} />
                            </div>
                          )}
                        </div>
                        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: COLORS.navy }}>
                          {school.name}
                        </h3>
                        <p style={{ margin: '0 0 8px', fontSize: 13, color: COLORS.grey, fontWeight: 500 }}>
                          📍 {school.city}
                        </p>
                        {school.tagline && (
                          <p style={{ margin: '0 0 20px', fontSize: 14, color: COLORS.grey, flex: 1 }}>
                            {school.tagline}
                          </p>
                        )}
                        <button
                          onClick={() => handleSelectSchool(school)}
                          style={{
                            background: school.primary_colour || COLORS.teal,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '10px 18px',
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            justifyContent: 'center',
                          }}
                        >
                          Register Here
                          <ArrowRightIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            )}
          </motion.div>
        )}

        {screen === 2 && selectedSchool && (
          <motion.div
            key="screen2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 60px' }}
          >
            {/* School header band */}
            <div style={{
              background: selectedSchool.primary_colour || COLORS.teal,
              borderRadius: '0 0 16px 16px',
              padding: '20px 28px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 32,
            }}>
              {selectedSchool.logo_url ? (
                <img
                  src={selectedSchool.logo_url}
                  alt={selectedSchool.name}
                  style={{ height: 48, borderRadius: 8, background: '#fff', padding: 4 }}
                />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: 10, background: 'rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <BuildingOffice2Icon style={{ width: 28, height: 28, color: '#fff' }} />
                </div>
              )}
              <div>
                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Registering with
                </p>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#fff' }}>
                  {selectedSchool.name}
                </h2>
              </div>
              {!autoSelected && (
                <button
                  onClick={() => setScreen(1)}
                  style={{
                    marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none',
                    borderRadius: 8, color: '#fff', padding: '6px 14px', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  ← Back
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} style={{
              background: '#fff', borderRadius: 16, padding: 32,
              boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
            }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: COLORS.navy }}>
                Student Registration
              </h2>
              <p style={{ margin: '0 0 6px', fontSize: 14, color: COLORS.grey }}>
                Step 1 of 2 — All fields are required unless marked optional.
              </p>
              <p style={{ margin: '0 0 22px', fontSize: 13, color: COLORS.teal, fontWeight: 600 }}>
                After this you will choose: Remote Online or Physical School
              </p>

              {/* Name row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>First Name</label>
                  <input
                    name="first_name" required value={formData.first_name}
                    onChange={handleFormChange} placeholder="Aisha"
                    style={inputStyle}
                  />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Last Name</label>
                  <input
                    name="last_name" required value={formData.last_name}
                    onChange={handleFormChange} placeholder="Karimova"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Date of Birth</label>
                <input
                  type="date" name="date_of_birth" required value={formData.date_of_birth}
                  onChange={handleFormChange} style={inputStyle}
                />
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email" name="email" required value={formData.email}
                  onChange={handleFormChange} placeholder="aisha@example.com"
                  style={inputStyle}
                />
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Phone Number</label>
                <input
                  type="tel" name="phone" required value={formData.phone}
                  onChange={handleFormChange} placeholder="+998 90 123 4567"
                  style={inputStyle}
                />
              </div>

              {/* ── Profile Photo Upload ─────────────────────────── */}
              <div style={fieldWrap}>
                <label style={labelStyle}>
                  Profile Photo <span style={{ fontWeight: 400, color: COLORS.grey }}>(optional — used for your School ID card)</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  {/* Preview circle */}
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%', border: `2px dashed ${photoPreview ? COLORS.teal : '#CBD5E1'}`,
                    background: photoPreview ? 'transparent' : '#F8FAFC',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0, position: 'relative',
                  }}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 28 }}>🧑</span>
                    )}
                    {photoUploading && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                          style={{ width: 24, height: 24, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/png,image/jpeg,image/webp"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setPhotoError('');
                        // Local preview
                        const reader = new FileReader();
                        reader.onload = ev => setPhotoPreview(ev.target.result);
                        reader.readAsDataURL(f);
                        setPhotoFile(f);
                        // Upload immediately
                        setPhotoUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('photo', f);
                          const res = await api.post('/api/public/upload-registration-photo', fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          setPhotoUrl(res.data.photo_url);
                        } catch (err) {
                          setPhotoError(err?.response?.data?.error || 'Failed to upload photo. You can continue without it.');
                          setPhotoPreview(null);
                          setPhotoFile(null);
                        } finally {
                          setPhotoUploading(false);
                        }
                      }}
                    />
                    <label htmlFor="photo-upload" style={{
                      display: 'inline-block', padding: '8px 16px', borderRadius: 8,
                      background: photoUrl ? '#F0FDF4' : '#EFF6FF',
                      border: `1.5px solid ${photoUrl ? '#86EFAC' : '#BFDBFE'}`,
                      color: photoUrl ? '#065F46' : '#1D4ED8',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>
                      {photoUrl ? '✓ Photo uploaded' : photoUploading ? 'Uploading…' : '📷 Choose Photo'}
                    </label>
                    {photoUrl && (
                      <button type="button" onClick={() => { setPhotoUrl(''); setPhotoPreview(null); setPhotoFile(null); }}
                        style={{ marginLeft: 8, background: 'none', border: 'none', color: COLORS.error, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                        Remove
                      </button>
                    )}
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: COLORS.grey }}>
                      JPG, PNG or WEBP · max 5 MB · clear face photo recommended
                    </p>
                    {photoError && <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.error }}>{photoError}</p>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Native Language</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      name="native_language" required value={formData.native_language}
                      onChange={handleFormChange}
                      style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      <option value="">Select…</option>
                      {NATIVE_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle}>Learning Goal</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      name="learning_goal" required value={formData.learning_goal}
                      onChange={handleFormChange}
                      style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      <option value="">Select…</option>
                      {LEARNING_GOALS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Programme Interest</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      name="programme_interest" required value={formData.programme_interest}
                      onChange={handleFormChange}
                      style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      <option value="">Select…</option>
                      {PROGRAMME_INTERESTS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>

                <div style={fieldWrap}>
                  <label style={labelStyle}>How did you hear about us?</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      name="referral_source" required value={formData.referral_source}
                      onChange={handleFormChange}
                      style={{ ...inputStyle, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      <option value="">Select…</option>
                      {REFERRAL_SOURCES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <ChevronDownIcon style={{ width: 16, height: 16, color: COLORS.grey, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Message <span style={{ fontWeight: 400, color: COLORS.grey }}>(optional)</span></label>
                <textarea
                  name="message" value={formData.message}
                  onChange={handleFormChange}
                  placeholder="Any questions or notes for the school?"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                />
              </div>

              {/* Payment info box */}
              <div style={{
                background: '#FFFBEB', border: `1px solid #FDE68A`,
                borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <CreditCardIcon style={{ width: 20, height: 20, color: COLORS.warning, flexShrink: 0, marginTop: 1 }} />
                <p style={{ margin: 0, fontSize: 13, color: '#92400E' }}>
                  <strong>💳 All fees are paid directly to your school — not to SpeakIQ.</strong>{' '}
                  SpeakIQ provides the technology platform only.
                </p>
              </div>

              {/* Identity checkbox */}
              <div style={{
                background: '#F0FDF4', border: `1px solid #BBF7D0`,
                borderRadius: 10, padding: '14px 16px', marginBottom: 24,
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="identity_confirmed"
                    checked={formData.identity_confirmed}
                    onChange={handleFormChange}
                    required
                    style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: COLORS.success }}
                  />
                  <span style={{ fontSize: 14, color: '#065F46', lineHeight: 1.5 }}>
                    I understand I will need to visit the centre in person to complete registration and verify my identity.
                  </span>
                </label>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2', border: `1px solid #FCA5A5`,
                  borderRadius: 8, padding: '10px 14px', marginBottom: 18,
                  color: COLORS.error, fontSize: 14,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%', padding: '13px 24px',
                  background: submitting ? '#94A3B8' : (selectedSchool.primary_colour || COLORS.teal),
                  color: '#fff', border: 'none', borderRadius: 10,
                  fontWeight: 700, fontSize: 16, cursor: submitting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.2s',
                }}
              >
                {submitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                      style={{ width: 18, height: 18, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                    />
                    Submitting…
                  </>
                ) : 'Next: Choose Learning Mode →'}
              </button>
            </form>
          </motion.div>
        )}

        {screen === 'otp' && (
          <motion.div key="screen-otp" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <div style={{ maxWidth: 480, margin: '40px auto', background: '#fff', borderRadius: 16, padding: 40, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
                <h2 style={{ color: COLORS.navy, fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Verify Your Email</h2>
                <p style={{ color: COLORS.grey, fontSize: 15, margin: 0 }}>
                  We sent a 6-digit code to <strong>{formData.email}</strong>.<br />Enter it below to continue.
                </p>
              </div>
              {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                placeholder="Enter 6-digit code"
                style={{ width: '100%', padding: '16px', fontSize: 28, letterSpacing: 12, textAlign: 'center', fontFamily: 'monospace', fontWeight: 800, border: '2px solid #CBD5E1', borderRadius: 10, boxSizing: 'border-box', outline: 'none', color: COLORS.navy }}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
              />
              <button
                onClick={handleVerifyOtp}
                disabled={submitting || otpCode.length < 6}
                style={{ width: '100%', marginTop: 16, padding: '14px', background: submitting || otpCode.length < 6 ? '#94A3B8' : COLORS.teal, color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: submitting || otpCode.length < 6 ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? 'Verifying…' : 'Verify & Continue →'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button
                  onClick={async () => {
                    setError(''); setOtpCode(''); setOtpSending(true);
                    try {
                      await api.post('/api/public/send-registration-otp', { email: formData.email, name: formData.first_name });
                    } catch(e) {}
                    setOtpSending(false);
                  }}
                  disabled={otpSending}
                  style={{ background: 'none', border: 'none', color: COLORS.teal, fontSize: 14, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {otpSending ? 'Sending…' : "Didn't receive it? Resend code"}
                </button>
                <br />
                <button onClick={() => { setScreen(2); setError(''); }} style={{ background: 'none', border: 'none', color: COLORS.grey, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', marginTop: 8 }}>
                  ← Back to form
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {screen === 3 && (
          <motion.div
            key="screen3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 60px' }}
          >
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.navy, margin: '0 0 10px' }}>
                How would you like to learn?
              </h1>
              <p style={{ fontSize: 15, color: COLORS.grey, margin: 0 }}>
                Choose your preferred learning mode — you can change this later from your profile.
              </p>
            </div>
            {error && (
              <div style={{ background: '#FEE2E2', color: COLORS.error, padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
              {[
                {
                  mode: 'remote',
                  icon: '🌐',
                  title: 'Remote Learning',
                  subtitle: 'Study from anywhere',
                  desc: 'Access all AI lessons, live tutor sessions, and homework online. Perfect for flexible schedules.',
                  color: COLORS.teal,
                },
                {
                  mode: 'physical',
                  icon: '🏫',
                  title: 'Physical School',
                  subtitle: 'Attend in person',
                  desc: 'Join group classes and tutor sessions at your school. Face-to-face learning for maximum engagement.',
                  color: '#10B981',
                },
              ].map(opt => (
                <motion.div
                  key={opt.mode}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => !submitting && handleFinalSubmit(opt.mode)}
                  style={{
                    background: formData.learning_mode === opt.mode ? opt.color : '#fff',
                    border: '2px solid ' + (formData.learning_mode === opt.mode ? opt.color : COLORS.teal + '40'),
                    borderRadius: 16,
                    padding: 28,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    boxShadow: formData.learning_mode === opt.mode ? '0 4px 16px ' + opt.color + '40' : '0 1px 4px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ fontSize: 52, marginBottom: 12 }}>{opt.icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 20, color: formData.learning_mode === opt.mode ? '#fff' : COLORS.navy, marginBottom: 4 }}>{opt.title}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: formData.learning_mode === opt.mode ? 'rgba(255,255,255,0.85)' : opt.color, marginBottom: 12 }}>{opt.subtitle}</div>
                  <p style={{ fontSize: 13, color: formData.learning_mode === opt.mode ? 'rgba(255,255,255,0.8)' : COLORS.grey, margin: 0, lineHeight: 1.5 }}>{opt.desc}</p>
                  {submitting && formData.learning_mode === opt.mode ? (
                    <div style={{ marginTop: 16, fontSize: 13, color: formData.learning_mode === opt.mode ? '#fff' : COLORS.teal, fontWeight: 600 }}>Submitting…</div>
                  ) : (
                    <div style={{
                      marginTop: 16, padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14,
                      background: formData.learning_mode === opt.mode ? 'rgba(255,255,255,0.2)' : opt.color,
                      color: '#fff', display: 'inline-block',
                    }}>
                      Choose {opt.title}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            <div style={{ textAlign: 'center' }}>
              <button onClick={() => setScreen(2)} style={{ background: 'none', border: 'none', color: COLORS.grey, fontSize: 14, cursor: 'pointer', textDecoration: 'underline' }}>
                ← Go back to form
              </button>
            </div>
          </motion.div>
        )}

        {screen === 4 && (
          <motion.div
            key="screen4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{ maxWidth: 640, margin: '0 auto', padding: '48px 24px 60px' }}
          >
            {/* Success card */}
            <div style={{
              background: '#fff', borderRadius: 20,
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}>
              {/* Green top band */}
              <div style={{ background: COLORS.success, padding: '32px 24px', textAlign: 'center' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
                >
                  <CheckCircleIcon style={{ width: 72, height: 72, color: '#fff', margin: '0 auto' }} />
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  style={{ margin: '16px 0 8px', fontSize: 28, fontWeight: 800, color: '#fff' }}
                >
                  Registration Submitted!
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: 16 }}
                >
                  Thank you, <strong>{formData.first_name}</strong>. Your registration has been received by{' '}
                  <strong>{selectedSchool?.name}</strong>.
                </motion.p>
              </div>

              <div style={{ padding: '28px 32px' }}>
                {/* Steps */}
                <h3 style={{ margin: '0 0 18px', fontSize: 16, fontWeight: 700, color: COLORS.navy }}>
                  What happens next?
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                  {[
                    { icon: '✅', label: 'Registration submitted', done: true },
                    { icon: '⏳', label: 'School reviews your application (usually within 24 hours)', done: false },
                    { icon: '📧', label: "You'll receive an email with your appointment", done: false },
                    { icon: '🏫', label: 'Visit the centre with valid ID documents', done: false },
                    { icon: '🎯', label: 'Complete your placement test at the centre', done: false },
                    { icon: '🎓', label: 'Receive your School ID card and start learning!', done: false },
                  ].map((step, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.07 }}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '10px 14px', borderRadius: 10,
                        background: step.done ? '#F0FDF4' : '#F8FAFC',
                        border: `1px solid ${step.done ? '#BBF7D0' : '#E2E8F0'}`,
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{step.icon}</span>
                      <span style={{ fontSize: 14, color: step.done ? '#065F46' : COLORS.navy, fontWeight: step.done ? 600 : 400, lineHeight: 1.5 }}>
                        {step.label}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* What to bring */}
                <div style={{
                  background: '#EFF6FF', border: `1px solid #BFDBFE`,
                  borderRadius: 12, padding: '16px 20px', marginBottom: 24,
                }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IdentificationIcon style={{ width: 18, height: 18 }} />
                    What to bring when you visit
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <li style={{ fontSize: 14, color: '#1E40AF' }}>Original passport or national ID card</li>
                    <li style={{ fontSize: 14, color: '#1E40AF' }}>Student card (if applicable)</li>
                    <li style={{ fontSize: 14, color: '#1E40AF' }}>Your phone (for your digital ID card)</li>
                  </ul>
                </div>

                {/* School contact */}
                {selectedSchool && (selectedSchool.phone || selectedSchool.contact_email) && (
                  <div style={{
                    background: '#F8FAFC', border: `1px solid #E2E8F0`,
                    borderRadius: 12, padding: '16px 20px', marginBottom: 24,
                  }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: COLORS.navy }}>
                      Contact {selectedSchool.name}
                    </h4>
                    {selectedSchool.phone && (
                      <p style={{ margin: '0 0 6px', fontSize: 14, color: COLORS.grey, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <PhoneIcon style={{ width: 15, height: 15 }} />
                        {selectedSchool.phone}
                      </p>
                    )}
                    {selectedSchool.contact_email && (
                      <p style={{ margin: 0, fontSize: 14, color: COLORS.grey, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <EnvelopeIcon style={{ width: 15, height: 15 }} />
                        {selectedSchool.contact_email}
                      </p>
                    )}
                  </div>
                )}

                <Link
                  to="/login"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '11px 24px', borderRadius: 10,
                    border: `2px solid ${COLORS.navy}`, color: COLORS.navy,
                    fontWeight: 700, fontSize: 14, textDecoration: 'none',
                    transition: 'background 0.2s',
                  }}
                >
                  Already received approval? Login here
                  <ArrowRightIcon style={{ width: 16, height: 16 }} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div style={{
        textAlign: 'center', padding: '24px', color: COLORS.grey,
        fontSize: 12, borderTop: '1px solid #E2E8F0', background: '#fff',
      }}>
        © {new Date().getFullYear()} SpeakIQ — English Learning Platform · Privacy Policy · Terms of Service
      </div>
    </div>
  );
}
